import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { ColumnsType } from 'antd/lib/table'
import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { IDetailedInfoFilter } from '../../model/types/DetailedInfoFilter.interface'
import { selectDetailedInfoFilters } from '../../model/domainOverviewSlice'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { PagesTableExpandedRow } from '../pages-table-expanded-row/PagesTableExpandedRow'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { useTranslation } from 'react-i18next'
import {
  AdvertisingResearchItems,
  GetResearchAdvertisingApiArg,
  ResearchAdvertisingArg,
  useGetResearchAdvertisingQuery,
} from '../../../../app/api'
import { PageCell } from '../../ui/cells/PageCell'
import { KeywordsCountCell } from '../../ui/cells/KeywordsCountCell'
import { AvgPositionCell } from '../../../../shared/ui/cells/AvgPositionCell'
import { LandingPageCell } from '../../../../shared/ui/cells/LandingPageCell'
import clsx from 'clsx'
import styles from './PagesTable.module.scss'

const Table = ({ onToggleExpand, isExpandAll, expandedRowKeys }: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const detailedInfoFilter = useAppSelector<IDetailedInfoFilter>(selectDetailedInfoFilters)
  const {
    project,
    site,
    country,
    engine_id,
    dateRange: date,
    keyword,
    has_brand,
    page_url,
    show_hidden,
    reversed_filters,
  } = detailedInfoFilter

  const [queryParams, setQueryParams] = useState<ResearchAdvertisingArg>()
  const { data, isLoading, isFetching } = useGetResearchAdvertisingQuery(
    queryParams as GetResearchAdvertisingApiArg,
    { skip: !project || queryParams == undefined },
  )

  useEffect(() => {
    if (!project) return

    setQueryParams({
      page: 1,
      'per-page': 10,
      projectId: +project,
      site,
      countryId: country ? +country : undefined,
      engineId: +engine_id,
      date,
      keywordId: keyword || undefined,
      hasBrand: has_brand,
      landingId: page_url || undefined,
      showHidden: show_hidden,
      reversedFilters: (reversed_filters || undefined) as unknown as string[],
    })
  }, [detailedInfoFilter])

  const columns: ColumnsType<AdvertisingResearchItems> = [
    {
      title: t('table.columns.page'),
      dataIndex: 'title',
      key: 'title',
      render: (_: string, record) => <PageCell {...record} />,
      width: '320px',
    },
    {
      title: () => (
        <span className={clsx(styles.columnTitle)}>
          {t('table.columns.keyword(s)')}
          <BpTooltip title={t('pages.domainOverview.keywordsTooltip')} />
        </span>
      ),
      dataIndex: 'keywords_count',
      key: 'keywords_count',
      render: (_: string, record) => (
        <KeywordsCountCell
          {...record}
          expandedRowKeys={expandedRowKeys}
          onToggleExpand={onToggleExpand}
        />
      ),
    },
    {
      title: () => (
        <span className={clsx(styles.columnTitle)}>
          {t('table.columns.avgPosition')}
          <BpTooltip title={t('pages.domainOverview.avgPositionTooltip')} />
        </span>
      ),
      dataIndex: 'position',
      key: 'position',
      render: (_: string, record) => <AvgPositionCell {...record} />,
      sorter: true,
    },
    {
      title: () => (
        <span className={clsx(styles.columnTitle)}>
          {t('table.columns.topKeyword')}
          <BpTooltip title={t('pages.domainOverview.topKeywordTooltip')} />
        </span>
      ),
      dataIndex: 'top_keyword',
      key: 'top_keyword',
    },
    {
      title: t('table.columns.landingPage'),
      dataIndex: 'link_screen_url',
      key: 'link_screen_url',
      render: (_: string, record) => <LandingPageCell {...record} />,
    },
    {
      title: t('table.columns.lastUpdated'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
  ]

  const handleTableChange = (
    { current, pageSize }: IBpPaginationProps,
    sorter?: SorterResult<AdvertisingResearchItems> | SorterResult<AdvertisingResearchItems>[],
  ) => {
    if (queryParams) {
      setQueryParams({
        ...queryParams,
        page: current,
        'per-page': pageSize,
        sort: useSorter(sorter),
      })
    }
  }

  return (
    <BpTable
      expandable={{
        expandedRowRender: (record: AdvertisingResearchItems) => (
          <PagesTableExpandedRow record={record} />
        ),
        expandedRowKeys: isExpandAll
          ? data?.data?.items?.map((item) => `${item.title}${item.last_found}`)
          : expandedRowKeys,
        showExpandColumn: false,
      }}
      columns={columns}
      data={data as TTableResponse<AdvertisingResearchItems>}
      dataSource={data?.data?.items}
      loading={isLoading || isFetching}
      rowKey={(record) => `${record.title}${record.last_found}`}
      onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
    ></BpTable>
  )
}

export const PagesTable = withExpanded(Table)
